import { LIST_WIDTH } from 'utils/constants/listview';
import { ATTENDANCE_TRACKING_SHIFT_STATUS } from 'utils/constants/attendanceTracking';
import { departmentFilter } from 'containers/Pages/People/Personnel/Layouts/options';
import {
  validationObject,
  validateRequired,
  validateLastActivity,
} from 'utils/validations';
import {
  fetchLastActivityTime,
  fetchNextActivityTime,
  maxDateRestrictor,
  minDateRestrictor,
} from '../Layouts/attendanceTrackingHelper';

export const attendanceTrackingColumns = [
  {
    headerName: 'Employee ID',
    field: 'employeeId',
    width: LIST_WIDTH.md,
  },
  {
    headerName: 'Name',
    field: 'employeeName',
    width: LIST_WIDTH.lg,
  },
  {
    headerName: 'Shift Status',
    field: 'shiftStatus',
    cellRenderer: 'activityComponent',
    width: LIST_WIDTH.sm,
  },
  {
    headerName: 'Start Shift',
    field: 'startShift',
    width: LIST_WIDTH.md,
  },
  {
    headerName: 'End Shift',
    field: 'endShift',
    width: LIST_WIDTH.md,
  },
  {
    headerName: 'Duration',
    field: 'duration',
    width: LIST_WIDTH.md,
  },
  {
    headerName: 'Reporter',
    field: 'reporterName',
    width: LIST_WIDTH.lg,
  },
  {
    field: 'departments',
    headerName: 'Departments',
    width: LIST_WIDTH.sm,
  },
];

export const activityTypeEnums = {
  startShift: 'START_SHIFT',
  endShift: 'END_SHIFT',
  startBreak: 'START_BREAK',
  endBreak: 'END_BREAK',
  available: 'AVAILABLE',
  unavailable: 'UNAVAILABLE',
  startTask: 'START_TASK',
  endTask: 'END_TASK',
  enterSite: 'ENTER_SITE',
  exitSite: 'EXIT_SITE',
  onBreak: 'ON_BREAK',
  onTask: 'ON_TASK',
};

export const filters = (attendanceList, userAssignedDepartments = []) => [
  {
    label: 'Personnel',
    field: 'personnel',
    type: 'select',
    options: attendanceList,
  },
  {
    label: 'Shift Status',
    field: 'shiftStatus',
    type: 'select',
    multiple: true,
    options: ATTENDANCE_TRACKING_SHIFT_STATUS,
  },
  {
    label: 'Date From',
    field: 'fromDate',
    type: 'date-picker',
  },
  {
    label: 'Date To',
    field: 'toDate',
    type: 'date-picker',
  },
  ...departmentFilter(userAssignedDepartments),
];

export const shiftColumn = [
  {
    field: 'activityType',
    headerName: 'Description',
    width: LIST_WIDTH.sm,
  },
  {
    field: 'activityTime',
    headerName: 'Time',
    width: LIST_WIDTH.sm,
  },
  {
    field: 'duration',
    headerName: 'Duration',
    width: LIST_WIDTH.sm,
  },
  {
    field: 'reporter',
    headerName: 'Reporter',
    width: LIST_WIDTH.sm,
  },
  {
    field: 'action',
    headerName: 'Action',
    cellRenderer: 'rowEditingBtn',
    width: LIST_WIDTH.default,
  },
];

export const isEditingActivityTypeAllowed = activityType => {
  switch (activityType) {
    case activityTypeEnums.startBreak:
    case activityTypeEnums.endBreak:
    case activityTypeEnums.startShift:
    case activityTypeEnums.endShift:
      return true;
    default:
      return false;
  }
};

export const isDeletingActivityTypeAllowed = activityType => {
  switch (activityType) {
    case activityTypeEnums.startBreak:
    case activityTypeEnums.endBreak:
    case activityTypeEnums.endShift:
      return true;
    default:
      return false;
  }
};

const availableNewActivityType = lastActivity => {
  switch (lastActivity.activityType) {
    case activityTypeEnums.startShift:
    case activityTypeEnums.endTask:
    case activityTypeEnums.enterSite:
    case activityTypeEnums.exitSite:
    case activityTypeEnums.endBreak:
      return [
        {
          id: activityTypeEnums.endShift,
          name: 'Clock Off',
        },
        {
          id: activityTypeEnums.startBreak,
          name: 'Start Break',
        },
      ];
    case activityTypeEnums.startBreak:
      return [
        {
          id: activityTypeEnums.endBreak,
          name: 'Finish Break',
        },
      ];
    default:
      return [];
  }
};

export const createFormFields = ({
  shiftDetails,
  lastActivity,
  editMode,
  reorderedActivities,
}) => {
  return [
    {
      field: 'activityType',
      type: 'select',
      size: 'full',
      label: 'Activity Type',
      options: editMode
        ? shiftDetails.activityType
        : availableNewActivityType(lastActivity),
      required: true,
      disabled: editMode,
    },

    {
      field: 'activityTime',
      type: 'datetime',
      size: 'full',
      label: 'Activity Time',
      minDate: editMode
        ? minDateRestrictor(
            shiftDetails.activityType,
            fetchLastActivityTime(shiftDetails, reorderedActivities),
          )
        : minDateRestrictor(
            shiftDetails.activityType,
            lastActivity.activityTime,
          ),
      maxDate: editMode
        ? maxDateRestrictor(
            shiftDetails.activityType,
            fetchNextActivityTime(shiftDetails, reorderedActivities),
          )
        : undefined,
      required: true,
    },
  ];
};

export const validationSchema = ({
  lastActivity,
  currentActivity,
  activityType,
}) => {
  return validationObject({
    activityType: validateRequired(),
    ...validateLastActivity({ lastActivity, currentActivity, activityType }),
  });
};
